import { useState } from 'react';
import client from '@/api/client';
import type { Tenant } from '@/api/types';

export type PhoneMatchType = 'NONE' | 'ACTIVE_WITH_BED' | 'NO_PROFILE' | 'CHECKED_OUT';

export interface TenantPhoneLookupResult {
  matchType: PhoneMatchType;
  tenant: Tenant | null;
  hostelName: string | null;
  matchedFullName: string | null;
  matchedEmail: string | null;
}

/** Looks up a phone number before tenant admission, to steer the Owner/Supervisor to the
 *  right flow (swap beds / prefill known details / reactivate a checked-out tenant) instead
 *  of a raw "phone already in use" error. Triggered on demand (phone-field blur), not on
 *  every keystroke. */
export function useTenantPhoneLookup() {
  const [result, setResult] = useState<TenantPhoneLookupResult | null>(null);
  const [loading, setLoading] = useState(false);

  async function lookup(phone: string): Promise<TenantPhoneLookupResult | null> {
    if (!phone || phone.replace(/\D/g, '').length < 7) return null;
    setLoading(true);
    try {
      const res = await client
        .get(`/api/tenants/lookup-by-phone?phone=${encodeURIComponent(phone)}`)
        .then(r => r as unknown as TenantPhoneLookupResult);
      setResult(res.matchType === 'NONE' ? null : res);
      return res;
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResult(null);
  }

  return { result, loading, lookup, reset };
}
